<script setup lang="ts">
import { onMounted, ref } from 'vue';

/**
 * `beforeinstallprompt` fires once and only if the browser deems the app
 * installable; capturing it lets us offer an Install button at a sensible
 * moment instead of leaving it to the browser's own menu.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const deferred = ref<BeforeInstallPromptEvent | null>(null);
const dismissed = ref(localStorage.getItem('mbs.install.dismissed') === '1');

onMounted(() => {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred.value = event as BeforeInstallPromptEvent;
  });
  window.addEventListener('appinstalled', () => (deferred.value = null));
});

async function install() {
  const event = deferred.value;
  if (!event) return;
  await event.prompt();
  await event.userChoice;
  deferred.value = null;
}

function dismiss() {
  dismissed.value = true;
  localStorage.setItem('mbs.install.dismissed', '1');
}
</script>

<template>
  <div v-if="deferred && !dismissed" class="border-b border-brand-200 bg-brand-50 text-brand-900">
    <div class="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2 text-sm">
      <span>Install this app for offline registration at the desk.</span>
      <button class="rounded bg-brand-600 px-3 py-1 text-white hover:bg-brand-700" @click="install">
        Install
      </button>
      <button class="rounded border border-brand-300 px-2 py-1 hover:bg-brand-100" @click="dismiss">
        Not now
      </button>
    </div>
  </div>
</template>
